/**
 * Every tunable constant in the game lives here. Single source of truth.
 *
 * If a magic number appears anywhere outside this file, that is a bug.
 * Tuning the game means editing this file and nothing else.
 */

// ---------------------------------------------------------------------------
// Simulation loop
// ---------------------------------------------------------------------------

/** Simulation steps per second. */
export const TICK_RATE = 20;

/** Milliseconds per simulation step. */
export const TICK_MS = 1000 / TICK_RATE;

/** Seconds per simulation step. Always the `dt` handed to tick(). */
export const TICK_DT = 1 / TICK_RATE;

/** Spiral-of-death guard: never run more than this many steps in one frame. */
export const MAX_STEPS_PER_FRAME = 5;

// ---------------------------------------------------------------------------
// Generators
// ---------------------------------------------------------------------------

/**
 * Resting potential: thoughts per second the brain makes with nothing wired to
 * it. Without this a fresh save produces nothing and can never afford its first
 * Neuron. It runs through every multiplier like any other output, and becomes
 * irrelevant within the first minute.
 */
export const BASE_OUTPUT = '3';

/**
 * Cost of the nth unit is baseCost * COST_GROWTH^n.
 *
 * Paired with MILESTONE_MULT below.
 *
 * The milestone was x8 while it was the only thing keeping old rows worth
 * buying. Once the feed chain made the pyramid mandatory that job was done, and
 * x8 became actively harmful: with the base necessarily the largest tier, its
 * milestone compounded until it carried 100% of output and every rung above it
 * was decoration. Measured on a full 320/64/32/16/8/4/2/1 pyramid:
 *
 *   x8 -> base 100.0% of output     x3 -> base 72.8%     x2 -> base 2.0%
 *
 * At x2 the newest tier carries the rig and the base is infrastructure, which
 * is the shape the feed chain is describing.
 */
export const COST_GROWTH = 1.12;

/**
 * Every this many units of a tier, that tier's output doubles.
 *
 * This is what makes buying a specific row a decision rather than a shrug:
 * each row always shows a concrete next goal, and deepening a tier you already
 * own competes with opening a new one.
 */
export const MILESTONE_EVERY = 25;
/** See the note on COST_GROWTH: these two numbers are set as a pair. */
export const MILESTONE_MULT = 2;

/**
 * Feed chain.
 *
 * Every tier is fed by the tier below it: a Dendrite needs five Neurons behind
 * it, an Axon needs two Dendrites, and so on down to the base. Units without
 * enough beneath them still fire, but only at UNDERFED_RATE.
 *
 * The direction matters and was wrong the first time. Pointing it the other way
 * - a Dendrite granting capacity to Neurons - meant buying the higher tier was
 * free: three Dendrites handed out capacity for forty Neurons that did not
 * exist, and nothing ever made the base worth building. This way the pyramid is
 * mandatory, which is what makes a Neuron worth owning.
 *
 * Soft rather than a hard gate, deliberately: a hard requirement turns the buy
 * button into a locked door and makes Max-buy dangerous. Underfed units are
 * merely inefficient, so no purchase is ever wasted.
 */
export const UNDERFED_RATE = 0.4;

/**
 * Units of any tier that run unfed, so opening a new row is never dead on
 * arrival. Small on purpose - at ten, the first several tiers ran free and the
 * chain did not bite until far too late.
 */
export const FREE_UNFED = 2;

/**
 * Cell heat: what the rig costs to run, on top of what the clock costs.
 *
 * Charged against each tier's share of output rather than per unit. Per-unit
 * heat cannot work here - a Cerebrum outputs about 6e7 times a Neuron, so any
 * figure large enough to be felt at the bottom is instantly fatal at the top.
 * A share is bounded 0..1 at every scale.
 *
 * Higher tiers carry a lower factor, so they run cooler per spark. That is what
 * makes climbing the ladder necessary rather than optional: a rig leaning on
 * Neurons cooks itself, and the only way out is to move the work upward.
 *
 * Sized against COOLING_BASE. At 0.5 it swamped the entire cooling budget and
 * melted every run regardless of the clock; it has to be felt without being the
 * dominant term.
 */
export const CELL_HEAT = 0.16;

/** Units of a tier needed before the next one is revealed at all. */
export const UNLOCK_PREVIOUS_COUNT = 10;

/** A generator tier reveals itself once the player has held this fraction of its base cost. */
export const REVEAL_FRACTION = 0.4;

// ---------------------------------------------------------------------------
// Clock
// ---------------------------------------------------------------------------

/** Neurons owned before the clock lever comes off its spec limit. */
export const LEVER_UNLOCK_COUNT = 10;

export const CLOCK_MIN = 80;
export const CLOCK_DEFAULT = 100;

/** Base clock ceiling. Raised by the cap1 / cap2 skills. */
export const CLOCK_CAP_BASE = 300;

/** Output multiplier is (clock / 100) ^ CLOCK_EXP. Raised by the `tuned` skill. */
export const CLOCK_EXP = 1.5;

// ---------------------------------------------------------------------------
// Heat
// ---------------------------------------------------------------------------

export const HEAT_MIN = 0;
export const HEAT_MAX = 100;

/** Heat units generated per second at clock 100. */
export const HEAT_BASE = 0.35;

/** heatGen = HEAT_BASE * (clock / 100) ^ HEAT_EXP. Lowered by the `paste` skill. */
export const HEAT_EXP = 2.6;

/**
 * Heat units removed per second before skills.
 * Deliberately equal to HEAT_BASE so clock 100 is exactly break-even:
 * 100% is a safe indefinite idle with no special-casing.
 */
export const COOLING_BASE = 0.35;

/** Peak of the thermal output bonus: 1 + (heat/100)^2 * THERMAL_MAX. */
export const THERMAL_MAX = 0.6;

// ---------------------------------------------------------------------------
// The Void
// ---------------------------------------------------------------------------

/**
 * Intrusive thoughts. The longer the brain stays awake, the more crowd in.
 *
 * Deliberately a function of run time rather than of output. Output grows
 * exponentially within a run, so anything measured against it would be either
 * trivial or crushing within a minute. Time is the one axis that stays
 * comparable at every stage of the game.
 *
 * Holding them off costs heat, which is what stops this being a second game
 * bolted to the side of the first: the void pushes on the dial the player is
 * already reading.
 */

/**
 * Meltdowns before the intrusive thoughts show up at all.
 *
 * The opening has to teach one thing at a time. A new player needs to meet
 * generators, the clock and a meltdown before anything starts pushing back,
 * and measurably so: introducing the void from the first run cut the opening
 * runs short enough that the first meltdown no longer reached its target yield.
 * It arrives afterwards, as an escalation.
 */
export const VOID_AFTER_MELTDOWNS = 3;

/** Seconds a run holds the void at bay before it reaches even odds. */
export const VOID_HOLD_SECONDS = 150;

/** Heat per second added when the void has completely taken over. */
export const VOID_HEAT_MAX = 4;

/** The void is silent for this long at the start of a run. */
export const VOID_ONSET_SECONDS = 45;

/** Share above which the void starts costing heat. Below this it is only noise. */
export const VOID_PRESSURE_THRESHOLD = 0.5;

// ---------------------------------------------------------------------------
// Vent coolant
// ---------------------------------------------------------------------------

/** Heat removed by a vent, before skills. */
export const VENT_AMOUNT = 40;

/** Seconds of vent cooldown, before skills. */
export const VENT_COOLDOWN = 45;

// ---------------------------------------------------------------------------
// Meltdown / Grey Matter
// ---------------------------------------------------------------------------

/** Below this many thoughts in a run, a meltdown yields nothing. */
export const GM_MIN_THOUGHTS = '1e3';

export const GM_SCALE = 4;
export const GM_DIV = '1e5';
export const GM_EXP = 0.4;

// ---------------------------------------------------------------------------
// Offline progress
// ---------------------------------------------------------------------------

/** Seconds of offline progress banked, before the cold_storage skill. */
export const OFFLINE_CAP_SECONDS = 8 * 60 * 60;

/** Fraction of online output earned while away, before the cold_storage skill. */
export const OFFLINE_RATE = 0.6;

/** Gaps shorter than this are collected silently with no modal. */
export const OFFLINE_MODAL_MIN_SECONDS = 60;

/** A gap longer than this is treated as clock skew and rewards nothing. */
export const OFFLINE_SKEW_LIMIT_SECONDS = 30 * 24 * 60 * 60;

// ---------------------------------------------------------------------------
// Save
// ---------------------------------------------------------------------------

export const SAVE_KEY = 'brainfryer.overclock.save';
export const SAVE_BROKEN_KEY = 'brainfryer.overclock.save.broken';
export const AUTOSAVE_INTERVAL_SECONDS = 10;

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export const DEFAULT_AUTOMELT_THRESHOLD = 95;

// ---------------------------------------------------------------------------
// Presentation thresholds
// ---------------------------------------------------------------------------

/** Clock at which the warranty silkscreen appears on the panel. */
export const WARRANTY_CLOCK = 250;

/** Heat at which the brain starts accumulating permanent cracks for the run. */
export const CRACK_HEAT = 60;

/** Gauge band edges, and the CAUTION / CRITICAL state thresholds. */
export const HEAT_CAUTION = 60;
export const HEAT_CRITICAL = 85;

/** Header warning lamp: amber here, oxblood and faster at the second. */
export const LAMP_WARN_HEAT = 90;
export const LAMP_CRITICAL_HEAT = 97;

/** Screen shake begins here, at (heat - this) / 5 * 3 pixels. */
export const SHAKE_HEAT = 95;

/** UI text refresh rate. The canvas still renders every frame. */
export const UI_FPS = 15;

/** How long the screen stays dark during the meltdown sequence. */
export const MELTDOWN_BLACKOUT_MS = 600;
